import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import ProjectCard from "@/components/ProjectCard";
import QRCodeModal from "@/components/QRCodeModal";
import { useAuth } from "@/context/AuthContext";
import { MapPin, Phone, QrCode, ShareNetwork, HardHat } from "@phosphor-icons/react";
import AdSlot from "@/components/AdSlot";

export default function PublicProfile() {
    const { slug } = useParams();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [qrOpen, setQrOpen] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/builders/${slug}`);
                setData(data);
            } finally {
                setLoading(false);
            }
        })();
    }, [slug]);

    if (loading) {
        return <div className="max-w-5xl mx-auto px-6 py-16 text-overline text-neutral-500">Loading…</div>;
    }
    if (!data) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-24 text-center">
                <h1 className="font-display text-5xl font-black uppercase mb-3">Builder not found</h1>
                <p className="text-neutral-600 mb-6">The link you followed may be wrong or the builder hasn't published their gallery yet.</p>
                <Link to="/discover" className="btn-ghost inline-flex">Browse builders</Link>
            </div>
        );
    }
    const { builder, projects } = data;
    const isOwner = user?.id === builder.id || user?.slug === builder.slug;
    const profileUrl = `${window.location.origin}/b/${builder.slug}`;

    return (
        <div>
            {/* Hero strip */}
            <section className="border-b border-black bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid md:grid-cols-12 gap-8">
                    <div className="md:col-span-8">
                        <div className="text-overline text-[#E63946] mb-3 flex items-center gap-2">
                            <HardHat size={14} weight="fill" /> {builder.specialties?.length ? builder.specialties.join(" · ") : "Builder & Trade"}
                        </div>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.92] uppercase">
                            {builder.business_name || builder.name}
                        </h1>
                        {builder.business_name && builder.business_name !== builder.name && (
                            <div className="mt-3 text-sm tracking-widest uppercase text-neutral-600">{builder.name}</div>
                        )}
                        <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-neutral-700">
                            {builder.location && (
                                <div className="flex items-center gap-2">
                                    <MapPin size={16} weight="duotone" /> {builder.location}
                                </div>
                            )}
                            {builder.phone && (
                                <div className="flex items-center gap-2">
                                    <Phone size={16} weight="duotone" /> {builder.phone}
                                </div>
                            )}
                        </div>

                        {builder.bio && (
                            <p className="mt-6 max-w-2xl text-neutral-700 leading-relaxed whitespace-pre-line">{builder.bio}</p>
                        )}

                        <div className="mt-8 flex flex-wrap gap-3">
                            <button
                                onClick={() => setQrOpen(true)}
                                className="btn-primary"
                                data-testid="public-show-qr"
                            >
                                <QrCode size={16} weight="bold" /> Get the QR
                            </button>
                            <button
                                onClick={() => {
                                    if (navigator.share) navigator.share({ url: profileUrl, title: builder.business_name || builder.name });
                                    else navigator.clipboard.writeText(profileUrl);
                                }}
                                className="btn-ghost"
                            >
                                <ShareNetwork size={16} /> Share profile
                            </button>
                            {isOwner && (
                                <Link to="/dashboard" className="btn-ghost">
                                    Manage
                                </Link>
                            )}
                        </div>
                    </div>
                    <div className="md:col-span-4 flex md:justify-end">
                        <div className="w-full max-w-xs aspect-[4/5] bg-black text-white p-6 flex flex-col justify-between">
                            <div>
                                <div className="text-overline text-[#E63946] mb-2">Projects</div>
                                <div className="font-display text-7xl font-black leading-none">{projects.length}</div>
                            </div>
                            <div className="text-xs uppercase tracking-widest text-neutral-400">
                                Member since{" "}
                                {builder.created_at ? new Date(builder.created_at).toLocaleDateString("en-GB", { month: "short", year: "numeric" }) : "—"}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Projects gallery */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
                <div className="mb-10">
                    <div className="text-overline text-[#E63946] mb-2">Portfolio</div>
                    <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter uppercase">Recent work</h2>
                </div>

                {projects.length === 0 ? (
                    <div className="border border-dashed border-neutral-400 p-16 text-center">
                        <p className="text-neutral-600">This builder hasn't added any projects yet.</p>
                    </div>
                ) : (
                    <div className="gallery-grid">
                        {projects.map((p, i) => (
                            <ProjectCard key={p.id} project={p} hrefBase="/p" index={i} />
                        ))}
                    </div>
                )}

                <AdSlot slot="profile_inline" variant="banner" className="mt-16" />
            </section>

            <QRCodeModal
                open={qrOpen}
                onClose={() => setQrOpen(false)}
                profileUrl={profileUrl}
                builderName={builder.business_name || builder.name}
            />
        </div>
    );
}
